import { ArrowUpRight, BookOpen } from "lucide-react";
import type { Article } from "@/lib/clusters";

/**
 * Reusable "Related from the TruWe library" block.
 * Rendered as <nav> so crawlers treat it as internal navigation, which is
 * what makes hub-and-spoke linking readable to search engines.
 *
 * Uses plain <a href> because blog articles are flat file-based routes with
 * unique route strings per slug (blog.magnesium-benefits.tsx etc.). The
 * codebase blog index already links this way — keeping it consistent.
 */
export function RelatedReading({
  articles,
  heading = "Related from the TruWe library",
  eyebrow = "Keep reading",
}: {
  articles: Article[];
  heading?: string;
  eyebrow?: string;
}) {
  if (!articles.length) return null;
  return (
    <nav
      aria-label={heading}
      className="mt-14 rounded-3xl border border-border bg-card p-6 md:p-8"
    >
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
        <BookOpen className="h-3.5 w-3.5" />
        {eyebrow}
      </div>
      <h2 className="mt-2 font-display text-xl font-bold text-brand-deep md:text-2xl">
        {heading}
      </h2>
      <ul className="mt-5 grid gap-3 sm:grid-cols-2">
        {articles.map((a) => (
          <li key={a.slug}>
            <a
              href={`/blog/${a.slug}`}
              className="group flex h-full flex-col rounded-2xl border border-border bg-background p-5 transition hover:-translate-y-0.5 hover:border-brand/40 hover:shadow-md"
            >
              <h3 className="font-display text-base font-semibold text-brand-deep group-hover:text-brand">
                {a.title}
              </h3>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">{a.excerpt}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand">
                Read the guide <ArrowUpRight className="h-3.5 w-3.5" />
              </span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
